"use client";

import React, { useEffect, useState } from "react";
import { useLocale, useTranslations } from "next-intl";
import { usePathname } from "next/navigation";
import { Link } from "@/navigation";
import Image from "next/image";
import Search from "../Menu/search";
import NavUser from "@/components/User/nav-user";
import { useShoppingCartStore } from "@/lib/store/shoppingCart";
import CountBadge from "@/components/Badge/count-badge";
import { useShoppingCart } from "@/lib/hooks/useShoppingCart";
import { getInquiry } from "@/lib/utils/util";
import { useLoveStore } from "@/lib/store/love.store";
import { menus } from "@/lib/menu";
import clsx from "clsx";
import useMenuMobile from "@/store/useMenuMobile";
import Translate from "@/components/Translate";
import { type Blog } from "@/lib/@types/api/blog";
import { defaultLocale } from "@/config";
import { useCategory } from "@/context/CategoriesContext";

import { useCompareStore } from "@/lib/store/Compare.store";
import MobileMenuDrawer, { type MobileMenuItem } from "@/components/Header/MobileMenuDrawer";
import { useModalInquiryContext } from "@/context/ModalInquiryContext";

const MENU_ID = {
	product: 20,
	applications: 21,
	aboutUs: 4,
	blog: 3,
	service: 14,
	contactUs: 5,
} as const;

const MENU_ACTIVE_PATH_PREFIXES: Record<number, string[]> = {
	[MENU_ID.product]: ["/products", "/product"],
	[MENU_ID.applications]: ["/applications"],
	[MENU_ID.aboutUs]: ["/about-us"],
	[MENU_ID.blog]: ["/category", "/blog"],
	[MENU_ID.service]: ["/service"],
	[MENU_ID.contactUs]: ["/contact-us"],
};

const DROPDOWN_CLOSE_DELAY_MS = 120;

type DropdownLinkItem = {
	key: string | number;
	href: string;
	label: string;
};

const TopNavOne: React.FC = () => {
	const { findCheckout } = useShoppingCart();
	const t = useTranslations();

	const pathname = usePathname(); // 当前路径，例如 /en/blog/123
	const [currentSlug, setCurrentSlug] = useState("");
	const locale = useLocale();
	const { openMenuMobile, handleMenuMobile, closeMenuMobile } = useMenuMobile();
	const {openModalInquiry} = useModalInquiryContext();

	//喜欢
	const [loveCount, setLoveCount] = useState(0);
	const { loveIds } = useLoveStore();
	//对比
	const [CompareCount, setCompareCount] = useState(0);
	let { compareIds } = useCompareStore();
	// 购物车
	const { cartList } = useShoppingCartStore();
	const [count, setCount] = useState(0);
	const [Inquirycount, setInquirycount] = useState(0);
	//是否变色
	const [isVisible, setIsVisible] = useState<boolean>(false);
	// 保存鼠标悬停前的isVisible状态
	const [previousIsVisible, setPreviousIsVisible] = useState<boolean>(false);
	// 博客分类数据
	const [blogCategories, setBlogCategories] = useState<Blog.BlogCl[]>([]);
	const [openDropdownMenuId, setOpenDropdownMenuId] = useState<number | null>(null);
	const dropdownCloseTimerRef = React.useRef<NodeJS.Timeout | null>(null);
	const desktopNavElementRef = React.useRef<HTMLElement>(null);
	const desktopNavListRef = React.useRef<HTMLUListElement>(null);
	// 菜单文案过长（如俄语、西语）放不下时，自动收起为汉堡菜单，避免挤压 Logo 与右侧操作区
	const [isDesktopMenuCollapsed, setIsDesktopMenuCollapsed] = useState(false);
	// 产品分类
	const { categories } = useCategory();

	// 获取博客分类
	useEffect(() => {
		const fetchBlogCategories = async () => {
			try {
				const langCode = locale || defaultLocale;
				const url = `${process.env.NEXT_PUBLIC_LANGUAGE_LIST_URL}/blog/cls_filter`;
				const res = await fetch(url, {
					method: "POST",
					body: JSON.stringify({
						lang_code: { lang_code: langCode },
					}),
					headers: {
						"Content-Type": "application/json"
					},
				}).then((r) => r.json()) as Blog.GetBlogClsResp;
				
				if (res.code === 200 && res.detail?.cls_list) {
					setBlogCategories(res.detail.cls_list);
				}
			} catch (error) {
				console.error("Error fetching blog categories:", error);
			}
		};
		
		fetchBlogCategories();
	}, [locale]);

	useEffect(() => {
		// 1. 刷新购物车数据
		findCheckout("default-channel");

		const handleResize = () => {
			let Inquirydata = getInquiry();
			setInquirycount(Inquirydata.length);
		};
		handleResize();
		window.addEventListener("storage", handleResize);
		
		const handleScroll = () => {
			const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

			if (currentSlug == "/") {
				// 在首页，根据滚动位置设置 isVisible
				if (scrollTop > 1) {
					setIsVisible(true);
					setPreviousIsVisible(true); // 保存滚动时的状态
				} else {
					setIsVisible(false);
					setPreviousIsVisible(false); // 保存滚动时的状态
				}
			} else {
				setIsVisible(true);
				setPreviousIsVisible(true); // 保存滚动时的状态
			}
		};

		// 监听全屏滚动事件
		const handlePageScroll = (event: CustomEvent) => {
			if (currentSlug === "/") {
				setIsVisible(event.detail.isVisible);
				setPreviousIsVisible(event.detail.isVisible); // 保存滚动时的状态
			}
		};

		window.addEventListener("scroll", handleScroll);
		window.addEventListener("pageScroll", handlePageScroll as EventListener);
		
		// Cleanup function to remove event listener
		return () => {
			window.removeEventListener("storage", handleResize);
			window.removeEventListener("scroll", handleScroll);
			window.removeEventListener("pageScroll", handlePageScroll as EventListener);
		};
	}, [currentSlug]);

	useEffect(() => {
		return () => {
			if (dropdownCloseTimerRef.current) {
				clearTimeout(dropdownCloseTimerRef.current);
			}
		};
	}, []);

	useEffect(() => {
		const desktopNavElement = desktopNavElementRef.current;
		const desktopNavList = desktopNavListRef.current;
		if (!desktopNavElement || !desktopNavList) {
			return;
		}

		const updateDesktopMenuCollapsedState = () => {
			if (getComputedStyle(desktopNavElement).display === "none") {
				return;
			}
			setIsDesktopMenuCollapsed(desktopNavList.scrollWidth > desktopNavElement.clientWidth);
		};

		updateDesktopMenuCollapsedState();
		const resizeObserver = new ResizeObserver(updateDesktopMenuCollapsedState);
		resizeObserver.observe(desktopNavElement);
		resizeObserver.observe(desktopNavList);
		return () => resizeObserver.disconnect();
	}, [locale]);

	useEffect(() => {
		setCount(cartList?.lines?.length || 0);
	}, [cartList]);

	useEffect(() => {
		// console.log(pathname);
		setCurrentSlug(pathname);
		//设置其他页面颜色
		if (pathname == "/") {
			setIsVisible(false);
			setPreviousIsVisible(false); // 保存路径变化时的状态
		} else {
			setIsVisible(true);
			setPreviousIsVisible(true); // 保存路径变化时的状态
		}
	}, [pathname]);

	useEffect(() => {
		setLoveCount(() => {
			return loveIds.length > 99 ? 99 : loveIds.length;
		});
		setCompareCount(() => {
			return compareIds.length > 99 ? 99 : compareIds.length;
		});
	}, [loveIds, compareIds]);

	useEffect(() => {
		closeMenuMobile();
	}, [pathname]);

	const handleMouseEnter = () => {
		// 保存当前状态，然后设置为true
		setPreviousIsVisible(isVisible);
		setIsVisible(true);
	};

	const handleMouseLeave = () => {
		// 恢复到之前保存的状态
		setIsVisible(previousIsVisible);
	};

	// 工具函数：递归查找所有子分类 ID
	const getAllSubCategoryIds = (parentId: number, categories: Blog.BlogCl[]): number[] => {
		const directChildren = categories.filter(cat => cat.cls_parent === parentId);
		const allIds = [parentId];
		directChildren.forEach(child => {
			allIds.push(child.cls_id);
			const grandchildren = getAllSubCategoryIds(child.cls_id, categories);
			allIds.push(...grandchildren.filter(id => id !== child.cls_id));
		});
		return allIds;
	};

	// 工具函数：产品分类排序 - 按照 metadata 中的 sortNumber 排序
	const sortProductCategories = (edges: any[]) => {
		return [...edges].sort((a, b) => {
			// 从 metadata 数组中获取 sortNumber
			const getSortNumber = (node: any): number | null => {
				if (node.metadata && Array.isArray(node.metadata)) {
					const sortNumberMeta = node.metadata.find((item: any) => item.key === "sortNumber");
					if (sortNumberMeta && sortNumberMeta.value) {
						const num = Number(sortNumberMeta.value);
						return isNaN(num) ? null : num;
					}
				}
				// 如果 metadata 中没有，尝试使用 node.sortNumber 作为后备
				if (node.sortNumber) {
					const num = Number(node.sortNumber);
					return isNaN(num) ? null : num;
				}
				return null;
			};

			const sortNumberA = getSortNumber(a.node);
			const sortNumberB = getSortNumber(b.node);

			// 如果都没有 sortNumber，保持原顺序
			if (sortNumberA === null && sortNumberB === null) return 0;
			// 如果只有 A 有 sortNumber，A 排在前面
			if (sortNumberA !== null && sortNumberB === null) return -1;
			// 如果只有 B 有 sortNumber，B 排在前面
			if (sortNumberA === null && sortNumberB !== null) return 1;
			// 如果都有 sortNumber，按数值大小排序
			return sortNumberA! - sortNumberB!;
		});
	};

	// 工具函数：博客分类排序
	const sortBlogCategories = (categories: Blog.BlogCl[]) => {
		return [...categories].sort((a, b) => a.cls_id - b.cls_id);
	};

	const openDropdownMenu = (menuId: number) => {
		if (dropdownCloseTimerRef.current) {
			clearTimeout(dropdownCloseTimerRef.current);
			dropdownCloseTimerRef.current = null;
		}
		setOpenDropdownMenuId(menuId);
	};

	const scheduleDropdownMenuClose = () => {
		if (dropdownCloseTimerRef.current) {
			clearTimeout(dropdownCloseTimerRef.current);
		}
		dropdownCloseTimerRef.current = setTimeout(() => {
			setOpenDropdownMenuId(null);
			dropdownCloseTimerRef.current = null;
		}, DROPDOWN_CLOSE_DELAY_MS);
	};

	const isMenuItemActive = (menuId: number) => {
		const activePathPrefixes = MENU_ACTIVE_PATH_PREFIXES[menuId] ?? [];
		return activePathPrefixes.some(
			(pathPrefix) => currentSlug === pathPrefix || currentSlug.startsWith(`${pathPrefix}/`)
		);
	};

	// 计算分类数据（移到 map 外面，避免重复计算）
	const applicationsCategory = blogCategories.find(cat => cat.cls_slug === "applications");
	const applicationsSubCategoryIds = applicationsCategory 
		? getAllSubCategoryIds(applicationsCategory.cls_id, blogCategories)
		: [];
	const applicationsSubCategories = applicationsCategory 
		? blogCategories.filter(cat => cat.cls_parent === applicationsCategory.cls_id)
		: [];
	// 应用下拉：展平为所有三级分类（非 tab），用于下拉项与锚点跳转
	const applicationsThirdLevelCategories = applicationsCategory
		? sortBlogCategories(
				blogCategories.filter(cat => applicationsSubCategories.some(sec => sec.cls_id === cat.cls_parent))
			)
		: [];
	const blogCategoriesFiltered = sortBlogCategories(
		blogCategories.filter(cat => !applicationsSubCategoryIds.includes(cat.cls_id) && cat.cls_slug !== "applications")
	);
	const productCategoriesSorted = categories?.edges ? sortProductCategories(categories.edges) : [];

	const getDropdownItemsForMenu = (menuId: number): DropdownLinkItem[] => {
		switch (menuId) {
			case MENU_ID.product:
				return productCategoriesSorted.map((edge) => ({
					key: edge.node.id,
					href: `/products/${edge.node.slug}`,
					label: edge.node.translation?.name || edge.node.name,
				}));
			case MENU_ID.applications:
				return applicationsThirdLevelCategories.map((category) => ({
					key: category.cls_id,
					href: `/applications#${category.cls_slug}`,
					label: category.cls_name,
				}));
			case MENU_ID.aboutUs:
				return [
					{ key: "introduction", href: "/about-us#introduction", label: t("plsyy.About-Nav-Introduction") },
					{ key: "vision-mission", href: "/about-us#vision-mission", label: t("plsyy.About-Nav-Vision") },
					{ key: "technical-support", href: "/about-us#technical-support", label: t("plsyy.About-Nav-Technical") },
					{ key: "manufacturing-capabilities", href: "/about-us#manufacturing-capabilities", label: t("plsyy.About-Nav-Manufacturing") },
					{ key: "certificates", href: "/about-us#certificates", label: t("plsyy.About-Nav-Certificates") },
					{ key: "team-gallery", href: "/about-us#team-gallery", label: t("plsyy.About-Nav-Team") },
				];
			case MENU_ID.blog:
				return blogCategoriesFiltered.map((category) => ({
					key: category.cls_id,
					href: `/category/${category.cls_slug}`,
					label: category.cls_name,
				}));
			default: {
				const menuWithChildren = menus.find((menuItem) => menuItem.id === menuId);
				return (menuWithChildren?.children ?? [])
					.filter((childMenuItem) => childMenuItem.show)
					.map((childMenuItem) => ({
						key: childMenuItem.id,
						href: childMenuItem.link,
						label: t(childMenuItem.name),
					}));
			}
		}
	};

	const isHeaderSolid = isVisible;
	const visibleMenus = menus.filter((menuItem) => menuItem.show);
	const mobileMenuItems: MobileMenuItem[] = visibleMenus.map((menuItem) => ({
		id: menuItem.id,
		label: t(menuItem.name),
		href: menuItem.link,
		isLinkable: menuItem.hasSlug,
		isActive: isMenuItemActive(menuItem.id),
		subItems: getDropdownItemsForMenu(menuItem.id),
	}));
	const headerIconButtonClassName = clsx(
		"relative flex h-9 w-9 items-center justify-center rounded-full text-[19px] transition-colors duration-200 xl:h-10 xl:w-10",
		isHeaderSolid
			? "text-gray-700 hover:bg-mainColor/10 hover:text-mainColor"
			: "text-white hover:bg-white/15"
	);

	return (
		<>
			<div className="top-nav" onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
				<header
					className={clsx(
						"w-full transition-[background-color,box-shadow,border-color] duration-300 max-md:hidden",
						isHeaderSolid
							? "border-b border-gray-200/70 bg-white/95 shadow-[0_4px_24px_-12px_rgba(15,23,42,0.25)] backdrop-blur-md"
							: "border-b border-white/15 bg-gradient-to-b from-black/35 via-black/10 to-transparent"
					)}
				>
					<div className="mx-auto flex h-[75px] w-full max-w-[1920px] items-center gap-x-3 px-5 xl:gap-x-6 xl:px-10 2xl:gap-x-8 2xl:px-[7.5vw]">
						<button
							type="button"
							onClick={handleMenuMobile}
							aria-label={t("nav.MENU")}
							className={clsx(headerIconButtonClassName, "-ml-2", !isDesktopMenuCollapsed && "lg:hidden")}
						>
							<i className="ri-menu-line" aria-hidden="true" />
						</button>

						<Link href="/" aria-label="BLINCE" className="flex shrink-0 items-center">
							<Image
								src="/image/plsyy/logo.png"
								width={1633}
								height={345}
								alt="BLINCE"
								priority
								className={clsx(
									"h-6 w-auto transition-[filter] duration-300 xl:h-7 2xl:h-8",
									!isHeaderSolid && "brightness-0 invert"
								)}
							/>
						</Link>

						<nav
							ref={desktopNavElementRef}
							aria-label="Main"
							aria-hidden={isDesktopMenuCollapsed || undefined}
							className={clsx(
								"hidden h-full min-w-0 flex-1 justify-center lg:flex",
								isDesktopMenuCollapsed && "pointer-events-none invisible"
							)}
						>
							<ul ref={desktopNavListRef} className="flex h-full items-center xl:gap-x-1 2xl:gap-x-3">
								{visibleMenus.map((menuItem) => {
									const dropdownItems = getDropdownItemsForMenu(menuItem.id);
									const hasDropdown = dropdownItems.length > 0;
									const isDropdownOpen = hasDropdown && openDropdownMenuId === menuItem.id;
									const isActive = isMenuItemActive(menuItem.id);
									const isHighlighted = isActive || isDropdownOpen;

									const menuItemContentClassName = clsx(
										"group/menu relative flex h-full items-center gap-0.5 whitespace-nowrap px-2 text-[14px] font-medium tracking-[0.01em] transition-colors duration-200 xl:px-3 xl:text-[15px] 2xl:px-3.5 2xl:text-base",
										isHeaderSolid
											? isHighlighted ? "text-mainColor" : "text-gray-700 hover:text-mainColor"
											: isHighlighted ? "text-white" : "text-white/85 hover:text-white"
									);

									const menuItemContent = (
										<>
											<span>{t(menuItem.name)}</span>
											{hasDropdown && (
												<i
													className={clsx(
														"ri-arrow-down-s-line text-base leading-none opacity-70 transition-transform duration-200",
														isDropdownOpen && "rotate-180"
													)}
													aria-hidden="true"
												/>
											)}
											<span
												aria-hidden="true"
												className={clsx(
													"absolute inset-x-2 bottom-[18px] h-[2px] origin-center rounded-full transition-transform duration-300 xl:inset-x-3 2xl:inset-x-3.5",
													isHeaderSolid ? "bg-mainColor" : "bg-white",
													isHighlighted ? "scale-x-100" : "scale-x-0 group-hover/menu:scale-x-100"
												)}
											/>
										</>
									);

									return (
										<li
											key={menuItem.id}
											className="relative h-full"
											onMouseEnter={hasDropdown ? () => openDropdownMenu(menuItem.id) : undefined}
											onMouseLeave={hasDropdown ? scheduleDropdownMenuClose : undefined}
											onFocus={hasDropdown ? () => openDropdownMenu(menuItem.id) : undefined}
											onBlur={hasDropdown ? scheduleDropdownMenuClose : undefined}
										>
											{menuItem.hasSlug ? (
												<Link
													href={menuItem.link}
													className={menuItemContentClassName}
													aria-current={isActive ? "page" : undefined}
													aria-haspopup={hasDropdown || undefined}
													aria-expanded={hasDropdown ? isDropdownOpen : undefined}
												>
													{menuItemContent}
												</Link>
											) : (
												<button
													type="button"
													className={clsx(menuItemContentClassName, "cursor-default")}
													aria-haspopup={hasDropdown || undefined}
													aria-expanded={hasDropdown ? isDropdownOpen : undefined}
												>
													{menuItemContent}
												</button>
											)}

											{hasDropdown && (
												<div
													className={clsx(
														"absolute left-1/2 top-full z-50 -translate-x-1/2 pt-1 transition-all duration-200 ease-out",
														isDropdownOpen
															? "visible translate-y-0 opacity-100"
															: "pointer-events-none invisible -translate-y-1 opacity-0"
													)}
												>
													<ul className="min-w-[220px] max-w-[320px] overflow-hidden rounded-lg border border-gray-100 bg-white py-2 shadow-[0_12px_32px_-8px_rgba(15,23,42,0.22)]">
														{dropdownItems.map((dropdownItem) => (
															<li key={dropdownItem.key}>
																<Link
																	href={dropdownItem.href}
																	onClick={() => setOpenDropdownMenuId(null)}
																	className="group/item flex items-center justify-between gap-3 px-4 py-2.5 text-[14px] text-gray-700 transition-colors duration-150 hover:bg-mainColor/[0.06] hover:text-mainColor"
																>
																	<span className="truncate">{dropdownItem.label}</span>
																	<i
																		className="ri-arrow-right-s-line -translate-x-1 text-base opacity-0 transition-all duration-150 group-hover/item:translate-x-0 group-hover/item:opacity-100"
																		aria-hidden="true"
																	/>
																</Link>
															</li>
														))}
													</ul>
												</div>
											)}
										</li>
									);
								})}
							</ul>
						</nav>

						<div className="ml-auto flex shrink-0 items-center gap-x-0.5 xl:gap-x-1">
							<Search className={headerIconButtonClassName} />

							<Link aria-label="inquiry" href="/inquiry" className={headerIconButtonClassName}>
								<i className="ri-file-list-3-line" aria-hidden="true" />
								<CountBadge count={Inquirycount} />
							</Link>

							<div className="hidden lg:block">
								<NavUser>
									<button type="button" aria-label="account" className={headerIconButtonClassName}>
										<i className="ri-user-3-line" aria-hidden="true" />
									</button>
								</NavUser>
							</div>

							{process.env.NEXT_PUBLIC_IS_I18N == "true" && (
								<>
									<span
										aria-hidden="true"
										className={clsx("mx-1 h-5 w-px", isHeaderSolid ? "bg-gray-200" : "bg-white/30")}
									/>
									<Translate isVisible={isHeaderSolid} />
								</>
							)}
						</div>
					</div>
				</header>

				<div className="relative flex h-16 w-full items-center justify-between border-b border-gray-100 bg-white/95 px-2 shadow-[0_2px_12px_-6px_rgba(15,23,42,0.18)] backdrop-blur-md md:hidden">
					<button
						type="button"
						onClick={handleMenuMobile}
						aria-label={t("nav.MENU")}
						aria-expanded={openMenuMobile}
						className="flex h-11 w-11 items-center justify-center rounded-full text-[22px] text-gray-800 active:bg-gray-100"
					>
						<i className="ri-menu-line" aria-hidden="true" />
					</button>

					<Link href="/" aria-label="BLINCE" className="absolute left-1/2 -translate-x-1/2">
						<Image src="/image/plsyy/logo.png" width={1633} height={345} alt="BLINCE" priority className="h-6 w-auto" />
					</Link>

					<div className="flex items-center">
						<Search className="flex h-11 w-11 items-center justify-center rounded-full text-[20px] text-gray-800 active:bg-gray-100" />
						<button
							type="button"
							onClick={() => openModalInquiry(false)}
							aria-label={t("nav.Inquiry")}
							className="flex h-11 w-11 items-center justify-center rounded-full text-[20px] text-gray-800 active:bg-gray-100"
						>
							<i className="ri-message-3-line" aria-hidden="true" />
						</button>
					</div>
				</div>

				<MobileMenuDrawer
					isOpen={openMenuMobile}
					onClose={closeMenuMobile}
					menuItems={mobileMenuItems}
					inquiryCount={Inquirycount}
				/>
			</div>
		</>
	);
};

export default TopNavOne;
